import type { QuotaQueueInfo } from '../utils/quotaQueue';

import axiosInstance from './axiosInstance';
import type { WebsiteSkillPage } from './skillStoreService';

export interface Loop {
  id: string;
  model_id?: string | null;
  title: string;
  instructions: string;
  timezone: string;
  schedule_kind: 'once' | 'recurring';
  schedule_json: { cron?: string; run_at?: string | null; summary?: string };
  next_run_at: string | null;
  status: 'active' | 'paused' | 'completed' | 'archived';
  browser_profile_id: string | null;
  record_video: boolean;
  show_personal_fields?: boolean;
  watermark_enabled?: boolean;
  attachments?: LoopAttachment[];
  created_at: string;
  active_run_status?: 'queued' | 'running' | 'waiting_for_input' | 'stopping' | null;
  active_run_id?: string | null;
  run_count?: number;
  success_count?: number;
  failure_count?: number;
}

export interface LoopAttachment {
  id: string; name: string; mime_type: string; size_bytes: number; created_at: string;
  loop_id: string | null; download_url: string;
}

export interface BrowserProfile {
  id: string;
  name: string;
  public_key: string | globalThis.JsonWebKey;
  last_seen_at: string | null;
  status: 'online' | 'offline' | 'revoked';
  parallel_runs_supported?: boolean;
}

export interface LoopRuntimeStatus {
  ready: boolean;
  code: 'ready' | 'configuration_required' | 'worker_unavailable';
  message: string;
}

export interface BrowserConnectionResult {
  status: 'connected' | 'offline' | 'timeout' | 'unavailable';
  latency_ms?: number | null;
  message: string;
}

export interface LoopReadOptions {
  signal?: globalThis.AbortSignal;
  skipSafeRetry?: boolean;
}

export interface LoopMessage {
  id: string;
  role?: string;
  author?: string;
  content: string;
  created_at: string;
}

export interface LoopQuestion {
  id: string;
  browser_profile_id?: string;
  fields?: Array<{
    id?: string; node: number | string; label: string; kind: string; frame?: string | number; boxes?: number[];
  }>;
  prompt?: string;
  question?: string;
  domain?: string;
  kind?: string;
  label?: string;
  sensitive?: boolean;
  options?: string[];
}

export interface LoopRun {
  id: string;
  model_id?: string | null;
  record_video?: boolean | null;
  status: string;
  scheduled_for: string;
  started_at: string | null;
  completed_at: string | null;
  cost_usd: number;
  charged_credits: number;
  reserved_credits?: number;
  billed_at?: string | null;
  max_cost_usd?: number;
  markup_multiplier: number;
  /**
   * Mixed worker log entries; see LoopStreamLogEvent (`kind: 'stream'`), LoopModelCallLogEvent
   * (`kind: 'model_call'`) and LoopCostLogEvent (`kind: 'cost'`) for those shapes.
   */
  logs_json: Array<{ message?: string; type?: string; timestamp?: string; [key: string]: unknown }> | null;
  output_text: string | null;
  error: string | null;
  question_json: LoopQuestion | null;
  recording_url: string | null;
  /** Set while a queued run waits for its plan's hourly quota. */
  quota_queue?: QuotaQueueInfo | null;
  /** Set when the run was refused for credits before it started. */
  credit_refusal?: LoopCreditRefusal | null;
}

/**
 * Why a run was refused for credits, worded in the loop's language (`message`; `run.error` stays English),
 * and what would let it run: a cheaper model, credits, or a bigger plan.
 */
export interface LoopCreditRefusal {
  reason: 'balance_below_step' | 'step_above_limit' | 'zero_balance';
  language: string;
  message: string;
  model_id: string;
  model_name: string;
  balance_credits: number;
  /** About what one step costs on the refused model. */
  step_credits?: number;
  /** A model whose step the run budget covers. */
  suggested_model?: { id: string; name: string; step_credits: number };
  add_credits: boolean;
  upgrade_available: boolean;
}

/** Worker note written when the live browser stream drops or comes back. */
export interface LoopStreamLogEvent {
  kind: 'stream'; state: 'unavailable' | 'restored'; reason: string | null; after_step: number; at: string;
  [key: string]: unknown;
}

/** Tools the loop supervisor can run; each execution is billed and logged as a `call_type: 'tool'` call. */
export type LoopToolName = 'python' | 'search' | 'browser_code';

/** One billed call of a run (`LoopModelTrace.events()`): a model request or a supervisor tool execution. */
export interface LoopModelCallLogEvent {
  kind: 'model_call'; id?: string; trace_id?: string | null;
  /** `jev`, `supervisor`, a helper's name, or `tool` for a supervisor tool execution. */
  call_type: string;
  /** For tools: `python`, `web-search:<provider model>` or `browser-code`. */
  model?: string; provider?: string; physical_model?: string | null; requested_model?: string | null;
  routing_role?: string | null; status?: string; input_tokens?: number; output_tokens?: number;
  duration_ms?: number; credits?: number; cost_complete?: boolean; billing_pending?: boolean;
  error_summary?: string | null; at?: string;
  /** Set when `call_type` is `tool`; an unknown name still renders as a generic tool call. */
  tool?: LoopToolName | string | null;
  /** Why the supervisor ran the tool, plain text of at most 200 characters, never field values. */
  purpose?: string | null;
  [key: string]: unknown;
}

type LoopCostGroup = 'supervisor' | 'helper' | 'tool';
type LoopCostGroupFields = {
  [field in `${LoopCostGroup}_${'usd' | 'credits' | 'calls' | 'tokens' | 'unpriced'}`]: number;
};

/** The run's usage by call group (`runner.COST_FIELDS`); tool calls are not in `helper_*` or `text_*`. */
export interface LoopCostSummary extends LoopCostGroupFields {
  total_usd: number; total_credits: number;
  jev_usd: number; jev_credits: number; jev_requests: number; jev_input_tokens: number;
  text_usd: number; text_calls: number; text_tokens: number; unpriced_text_calls: number;
}

/** Last entry of a run's log; runs from before a group existed simply lack its fields. */
export type LoopCostLogEvent = { kind: 'cost'; [key: string]: unknown } & Partial<LoopCostSummary>;

export interface LoopMemory { id: string; domain: string; content: string; updated_at?: string; }

/** One learning call a run made when it missed its goal. */
export interface LoopSkillLearning {
  id: string; skill_id: string | null; page_id: string | null; domain: string; attempt: number;
  kind: 'create' | 'refine'; outcome: 'learned' | 'unachievable' | 'unaffordable' | 'failed';
  reason: string | null; credits: number; payer: 'user' | 'skill_fund' | 'skill_account' | 'none';
  created_at: string | null;
}

/** A website skill a run used or learned, with that run's use, its verdict and the learning calls. */
export interface LoopRunSkill {
  skill_id: string; domain: string; site_title: string; favicon_url: string | null;
  is_public: boolean; is_system: boolean; is_owner: boolean;
  learn_count: number; refine_count: number; last_learned_at: string | null; pages: WebsiteSkillPage[];
  usage: {
    id: string; credits: number; met_expectations: boolean | null;
    feedback_source: 'agent' | 'user' | 'system' | null; problem: string | null;
  } | null;
  learnings: LoopSkillLearning[];
}

export interface LoopRunSkills { skills: LoopRunSkill[]; unsaved_learnings: LoopSkillLearning[]; }
export type LoopRating = 'up' | 'down';
/** A comment (content) or the run's own thumbs up or down (a row with no content). */
export interface LoopFeedback {
  id: string; loop_id: string; run_id: string; content: string; created_at: string; rating?: LoopRating | null;
}
export interface LoopConversationCall {
  id: string;
  call_type: 'schedule';
  model: string;
  physical_model?: string | null;
  requested_model?: string | null;
  routing_role?: string | null;
  routing_request_id?: string | null;
  routing_leg_index?: number | null;
  status: string;
  credits: number | null;
  input_tokens: number;
  output_tokens: number;
  duration_ms: number;
  cost_complete: boolean;
  trace_id: string | null;
  at: string;
  billing_pending: boolean;
}
export interface SecretEnvelope {
  algorithm: 'RSA-OAEP-256+A256GCM';
  encrypted_key: string;
  iv: string;
  ciphertext: string;
  aad: string;
}
export interface LoopAnswer {
  question_id?: string;
  answer?: string;
  envelope?: SecretEnvelope;
  browser_profile_id?: string;
  save?: boolean;
  kind?: string;
  domain?: string;
  label?: string;
}
export type LoopPatch = Partial<Pick<Loop,
  'title' | 'instructions' | 'browser_profile_id' | 'record_video' | 'status' | 'schedule_json' | 'schedule_kind' | 'timezone'
  | 'model_id' | 'watermark_enabled' | 'show_personal_fields'>> & { attachment_ids?: string[] };

const BASE = '/api/v1/loops';
const loopPath = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export const loopService = {
  list: async (options?: LoopReadOptions): Promise<Loop[]> => (await axiosInstance.get(BASE, options)).data,
  get: async (id: string, options?: LoopReadOptions): Promise<Loop> => (
    await axiosInstance.get(loopPath(id), options)
  ).data,
  runtimeStatus: async (options?: LoopReadOptions): Promise<LoopRuntimeStatus> => (
    await axiosInstance.get(`${BASE}/runtime-status`, options)
  ).data,
  create: async (data: {
    message: string; timezone: string; browser_profile_id?: string; record_video?: boolean; model_id?: string;
    watermark_enabled?: boolean; attachment_ids?: string[];
  }): Promise<Loop> => (await axiosInstance.post(BASE, data)).data,
  uploadAttachment: async (file: File, loopId?: string): Promise<LoopAttachment> => {
    const data = new FormData();

    data.append('file', file);
    if (loopId) data.append('loop_id', loopId);

    return (await axiosInstance.post(`${BASE}/attachments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  attachmentMetadata: async (ids: string[]): Promise<LoopAttachment[]> => (
    await axiosInstance.get(`${BASE}/attachments`, { params: { ids }, paramsSerializer: { indexes: null } })
  ).data,
  removeAttachment: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/attachments/${encodeURIComponent(id)}`);
  },
  downloadAttachment: async (id: string): Promise<globalThis.Blob> => (
    await axiosInstance.get(`${BASE}/attachments/${encodeURIComponent(id)}`, { responseType: 'blob' })
  ).data,
  update: async (id: string, data: LoopPatch): Promise<Loop> => (await axiosInstance.patch(loopPath(id), data)).data,
  archive: async (id: string): Promise<void> => { await axiosInstance.delete(loopPath(id)); },
  messages: async (id: string, options?: LoopReadOptions): Promise<LoopMessage[]> => (
    await axiosInstance.get(`${loopPath(id)}/messages`, options)
  ).data,
  conversationCalls: async (id: string, options?: LoopReadOptions): Promise<LoopConversationCall[]> => (
    await axiosInstance.get(`${loopPath(id)}/conversation-calls`, options)
  ).data,
  send: async (id: string, content: string): Promise<LoopMessage> => (
    await axiosInstance.post(`${loopPath(id)}/messages`, { content })
  ).data,
  runs: async (id: string, options?: LoopReadOptions): Promise<LoopRun[]> => (
    await axiosInstance.get(`${loopPath(id)}/runs`, options)
  ).data,
  run: async (id: string): Promise<LoopRun> => (await axiosInstance.post(`${loopPath(id)}/run`)).data,
  stop: async (id: string): Promise<{ loop: Loop; runs: LoopRun[] }> => (
    await axiosInstance.post(`${loopPath(id)}/stop`)
  ).data,
  profiles: async (options?: LoopReadOptions): Promise<BrowserProfile[]> => (
    await axiosInstance.get(`${BASE}/browser-profiles`, options)
  ).data,
  pair: async (name: string): Promise<{ pairing_code: string; expires_at: string }> => (
    await axiosInstance.post(`${BASE}/browser-profiles/pairing`, { name })
  ).data,
  revoke: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/browser-profiles/${encodeURIComponent(id)}`);
  },
  testProfileConnection: async (id: string, signal?: globalThis.AbortSignal): Promise<BrowserConnectionResult> => (
    await axiosInstance.post(`${BASE}/browser-profiles/${encodeURIComponent(id)}/test`, {}, { signal })
  ).data,
  memories: async (): Promise<LoopMemory[]> => (await axiosInstance.get(`${BASE}/memories`)).data,
  remember: async (domain: string, content: string): Promise<void> => {
    await axiosInstance.post(`${BASE}/memories`, { domain, content });
  },
  forget: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/memories/${encodeURIComponent(id)}`);
  },
  answer: async (runId: string, data: LoopAnswer): Promise<void> => {
    await axiosInstance.post(`${BASE}/runs/${encodeURIComponent(runId)}/answer`, data);
  },
  recording: async (runId: string, signal?: globalThis.AbortSignal): Promise<globalThis.Blob> => (
    await axiosInstance.get(`${BASE}/runs/${encodeURIComponent(runId)}/recording`, { responseType: 'blob', signal })
  ).data,
  preview: async (runId: string, signal?: globalThis.AbortSignal): Promise<{
    image: globalThis.Blob | null; status: string; reason?: string;
  }> => {
    const options = { responseType: 'blob' as const, signal, skipSafeRetry: true };
    const response = await axiosInstance.get(`${BASE}/runs/${encodeURIComponent(runId)}/preview`, options);

    return { image: response.status === 200 ? response.data : null,
      status: response.headers['x-loop-preview-status'] || (response.status === 200 ? 'live' : 'waiting'),
      reason: response.headers['x-loop-preview-reason'] };
  },
  feedback: async (runId: string, signal?: globalThis.AbortSignal): Promise<LoopFeedback[]> => (
    await axiosInstance.get(`${BASE}/runs/${encodeURIComponent(runId)}/feedback`, { signal })
  ).data,
  /** The extension version this deployment offers for download; available is false when it ships none. */
  extensionInfo: async (): Promise<{ available: boolean; version: string | null }> => (
    await axiosInstance.get(`${BASE}/extension/info`)
  ).data,
  runSkills: async (runId: string, signal?: globalThis.AbortSignal): Promise<LoopRunSkills> => (
    await axiosInstance.get(`${BASE}/runs/${encodeURIComponent(runId)}/skills`, { signal })
  ).data,
  saveFeedback: async (runId: string, content: string, rating?: LoopRating | null): Promise<LoopFeedback> => (
    await axiosInstance.post(`${BASE}/runs/${encodeURIComponent(runId)}/feedback`, { content, rating: rating ?? null })
  ).data,
  rate: async (runId: string, rating: LoopRating | null): Promise<{ run_id: string; rating: LoopRating | null }> => (
    await axiosInstance.put(`${BASE}/runs/${encodeURIComponent(runId)}/rating`, { rating })
  ).data,
  pricing: async (): Promise<{ cost_multiplier: number }> => (
    await axiosInstance.get('/api/v1/admin/settings/loops')
  ).data,
  updatePricing: async (cost_multiplier: number): Promise<void> => {
    await axiosInstance.put('/api/v1/admin/settings/loops', { cost_multiplier });
  },
};
